import { isNil } from 'lodash';

import { freeze } from '@reduxjs/toolkit';
import {
  ChartSortEnum,
  IChartSettings,
  IDisplayedMeasureSetting,
  IExpressionSettings,
} from '../../../components/Chart/interfaces';
import { IExpressionAnalysis } from '../../../utils/apollo/responseTypes';
import {
  defaultTableSettings,
  generateDisplayedSliceSettings,
  mapDefaultChartKindToChartType,
  mapMeasureControl,
} from './utils';
import { UnreachableCaseError } from 'ts-essentials';
import { assertNotNil } from '../../../utils/types';
import { CHART_TYPES } from '../../../components/Chart/constants/chartTypes';
import { initialChartSettings } from './utils';
import { Geo, TimeUnit } from '../../../utils/apollo/graphqlSchema';
import { globalFormat } from '../../../utils/formatting';
import {
  CHART_DATE_FORMAT,
  DEFAULT_RESULT_MEASURE_DECIMALS,
} from '../../../config/constants';

export const defaultExpressionSettings = freeze<IExpressionSettings>(
  {
    showMeasure: true,
    showCompare: false,
    lineType: '',
  },
  true,
);

const generateExpressionDisplayedMeasure = (
  expression: IExpressionAnalysis,
): IDisplayedMeasureSetting[] => {
  const { chartControl, query } = expression;

  const measureControls = chartControl.measureControls;

  // There are 2 possible scenarios:
  // 1. Simple measure with a single measure control;
  // 2. Compound measure with multiple controls w/o result measure control.
  // First output element0 should be visible by default
  if (query.measure.__typename === 'SimpleMeasure') {
    const measureControl = measureControls[0];

    return [
      {
        ...mapMeasureControl({
          measureControl,
          format: query.measure?.format,
        }),
        isVisible: true,
      },
    ];
  } else if (query.measure.__typename === 'CompoundMeasure') {
    const { measure } = query;
    const { measures } = measure;

    return measureControls
      .reduce<IDisplayedMeasureSetting[]>((acc, measureControl) => {
        if (measureControl.isResultKey) {
          // if a result measure with division formula, apply decimal format to the measure
          // to avoid display integer on floats.
          const decimalPlace = measure.formula?.includes('/')
            ? DEFAULT_RESULT_MEASURE_DECIMALS
            : undefined;
          const mappedValue = mapMeasureControl({
            measureControl,
            format: measure?.format,
            decimalPlace,
          });
          // Put result measure in the beginning of array
          acc.unshift(mappedValue);
        } else {
          const foundMeasure = measures.find(
            (simpleMeasure) => simpleMeasure.key === measureControl.key,
          );

          const mappedValue = mapMeasureControl({
            measureControl,
            format: foundMeasure?.format ?? null,
          });

          acc.push(mappedValue);
        }

        return acc;
      }, [])
      .map((displayedMeasureSetting, index) => {
        // First element should be visible by default
        if (index === 0) {
          return {
            ...displayedMeasureSetting,
            isVisible: true,
          };
        } else {
          return displayedMeasureSetting;
        }
      });
  } else {
    throw new UnreachableCaseError(query.measure);
  }
};

// generate default chart settings from analysis data.
export const generateExpressionDefaultSettings = (
  analysis: IExpressionAnalysis,
): IChartSettings => {
  const defaultChartType = mapDefaultChartKindToChartType(
    analysis.chartControl.default,
  );
  // For Expression, we select all slices and the first measure
  const displayedMeasureSettings = generateExpressionDisplayedMeasure(analysis);
  const displayedSliceSettings = generateDisplayedSliceSettings({
    analysis,
  });
  assertNotNil(analysis.data);
  // if there is only one date and one measure in chart, use single day label
  let defaultSingleDayLabel = '';
  if (
    analysis.data.dates.length === 1 &&
    displayedMeasureSettings.length === 1 &&
    defaultChartType === CHART_TYPES.BAR_CHART
  ) {
    defaultSingleDayLabel = analysis.data.dates[0];
  }

  if (
    analysis.timeUnit === TimeUnit.AllTime &&
    // Histogram analyses don't have dates in the `dates` field but bucket ranges
    // like: "[-∞, 0)", "[0, 2)". Setting the singleDayLabel to trigger single
    // day view doesn't make sense for them, thus we are skipping
    !analysis.query.subjectBucketDef &&
    analysis.geo === Geo.Na
  ) {
    defaultSingleDayLabel = `${globalFormat.formatDate(
      analysis.start,
      CHART_DATE_FORMAT,
    )} ~ ${globalFormat.formatDate(analysis.end, CHART_DATE_FORMAT)}`;
  }

  // if geo map, auto set the single day label to the first date
  if (analysis.timeUnit !== TimeUnit.AllTime && analysis.geo !== Geo.Na) {
    defaultSingleDayLabel = analysis.data.dates[0];
  }

  // for geo map, we need to set the first measure key as the displayed measure in GeoConfig
  let geoMapConfig = initialChartSettings.geoMapConfig;
  if (analysis.geo !== Geo.Na) {
    const measureControl = analysis.chartControl.measureControls.find(
      (mc) => mc.isResultKey,
    );

    const measureKey = measureControl
      ? measureControl.key
      : analysis.chartControl.measureControls[0].key;
    assertNotNil(measureKey);
    geoMapConfig = {
      ...initialChartSettings.geoMapConfig,
      displayedMeasureKey: measureKey,
    };
  }

  const expressionSettings: IExpressionSettings = {
    ...defaultExpressionSettings,
    showCompare:
      !isNil(analysis?.comparisonData) && !analysis.comparisonData.isEmpty,
  };

  return {
    ...initialChartSettings,
    chartType: defaultChartType,
    displayedSliceSettings,
    displayedMeasureSettings,
    singleDayLabel: defaultSingleDayLabel,
    sort: ChartSortEnum.NONE,
    expressionSettings,
    tableSettings: defaultTableSettings,
    geoMapConfig,
  };
};
